/**
 * Hook for fetching and managing task templates.
 */

import { useState } from "react";
import { gql, useQuery, useMutation } from "@apollo/client";
import type { ExcelData, Subtask } from "@/components/taskTemplate/TaskTemplateImportAndValidation";
import { TaskAreaType } from "@/types";

const GET_TASK_TEMPLATE_SETS = gql`
  query GetTaskTemplateSets($filter: TaskTemplateSetFilterInput, $limit: Int, $offset: Int) {
    taskTemplateSets(filter: $filter, limit: $limit, offset: $offset) {
      items {
        id
        name
        description
        createdByUserId
        createdBy {
          id
          firstName
          lastName
          email
        }
        version
        createdAt
        updatedAt
        isEditable
        linkedProjects {
          id
          name
          status
        }
      }
      totalCount
      hasNextPage
    }
  }
`;

const CREATE_TASK_TEMPLATE_SET = gql`
  mutation CreateTaskTemplateSet($input: TaskTemplateSetCreateInput!) {
    createTaskTemplateSet(input: $input) {
      success
      message
      taskTemplateSet {
        id
        name
        description
        createdByUserId
        version
        createdAt
        updatedAt
      }
    }
  }
`;

const UPDATE_TASK_TEMPLATE_SET = gql`
  mutation UpdateTaskTemplateSet($input: TaskTemplateSetUpdateInput!) {
    updateTaskTemplateSet(input: $input) {
      success
      message
      taskTemplateSet {
        id
        name
        description
        version
        updatedAt
      }
    }
  }
`;

const DUPLICATE_TASK_TEMPLATE_SET = gql`
  mutation DuplicateTaskTemplateSet($input: TaskTemplateSetDuplicateInput!) {
    duplicateTaskTemplateSet(input: $input) {
      success
      message
      taskTemplateSet {
        id
        name
        description
        version
        createdAt
      }
    }
  }
`;

const SAVE_TEMPLATE_SET_TASKS = gql`
  mutation SaveTemplateSetTasks(
    $taskTemplateSetId: String!
    $creates: [TemplateTaskDraftCreateInput!]!
    $updates: [TemplateTaskDraftUpdateInput!]!
    $deleteIds: [String!]!
  ) {
    saveTemplateSetTasks(
      taskTemplateSetId: $taskTemplateSetId
      creates: $creates
      updates: $updates
      deleteIds: $deleteIds
    ) {
      success
      message
      errors
      createdCount
      updatedCount
      deletedCount
    }
  }
`;

const DELETE_TASK_TEMPLATE_SET = gql`
  mutation DeleteTaskTemplateSet($id: String!) {
    deleteTaskTemplateSet(id: $id) {
      success
      message
    }
  }
`;

const BULK_CREATE_TEMPLATE_TASKS = gql`
  mutation BulkCreateTemplateTasks(
    $taskTemplateSetId: String!
    $tasks: [TemplateTaskCreateInput!]!
  ) {
    bulkCreateTemplateTasks(
      taskTemplateSetId: $taskTemplateSetId
      tasks: $tasks
    ) {
      success
      message
      createdCount
      templateTasks {
        id
        name
        templateWorkSectionId
        templateProcurementPackageId
        parentTemplateTaskId
      }
    }
  }
`;

const BULK_DELETE_TEMPLATE_TASKS_BY_SET = gql`
  mutation BulkDeleteTemplateTasksBySet($taskTemplateSetId: String!) {
    bulkDeleteTemplateTasksBySet(taskTemplateSetId: $taskTemplateSetId) {
      success
      message
    }
  }
`;

const BULK_DELETE_TEMPLATE_PROCUREMENT_PACKAGES_BY_SET = gql`
  mutation BulkDeleteTemplateProcurementPackagesBySet($taskTemplateSetId: String!) {
    bulkDeleteTemplateProcurementPackagesBySet(taskTemplateSetId: $taskTemplateSetId) {
      success
      message
    }
  }
`;

const BULK_DELETE_TEMPLATE_WORK_SECTIONS_BY_SET = gql`
  mutation BulkDeleteTemplateWorkSectionsBySet($taskTemplateSetId: String!) {
    bulkDeleteTemplateWorkSectionsBySet(taskTemplateSetId: $taskTemplateSetId) {
      success
      message
    }
  }
`;

const CREATE_PROCUREMENT_PACKAGE = gql`
  mutation CreateTemplateProcurementPackage($input: TemplateProcurementPackageCreateInput!) {
    createTemplateProcurementPackage(input: $input) {
      success
      message
      templateProcurementPackage {
        id
        code
        name
        taskTemplateSetId
        version
      }
    }
  }
`;

const CREATE_WORK_SECTION = gql`
  mutation CreateTemplateWorkSection($input: TemplateWorkSectionCreateInput!) {
    createTemplateWorkSection(input: $input) {
      success
      message
      templateWorkSection {
        id
        code
        name
        taskTemplateSetId
        version
      }
    }
  }
`;

const UPDATE_WORK_SECTION = gql`
  mutation UpdateTemplateWorkSection($input: TemplateWorkSectionUpdateInput!) {
    updateTemplateWorkSection(input: $input) {
      success
      message
      templateWorkSection {
        id
        code
        name
        templateProcurementPackageId
        version
      }
    }
  }
`;

const BULK_CREATE_TEMPLATE_PROCUREMENT_PACKAGES = gql`
  mutation BulkCreateTemplateProcurementPackages(
    $taskTemplateSetId: String!
    $packages: [TemplateProcurementPackageBulkCreateInput!]!
  ) {
    bulkCreateTemplateProcurementPackages(
      taskTemplateSetId: $taskTemplateSetId
      packages: $packages
    ) {
      success
      message
      createdCount
      templateProcurementPackages {
        id
        code
        name
        taskTemplateSetId
        version
      }
    }
  }
`;

const BULK_CREATE_TEMPLATE_WORK_SECTIONS = gql`
  mutation BulkCreateTemplateWorkSections(
    $taskTemplateSetId: String!
    $workSections: [TemplateWorkSectionBulkCreateInput!]!
  ) {
    bulkCreateTemplateWorkSections(
      taskTemplateSetId: $taskTemplateSetId
      workSections: $workSections
    ) {
      success
      message
      createdCount
      templateWorkSections {
        id
        code
        name
        taskTemplateSetId
        templateProcurementPackageId
        version
      }
    }
  }
`;

export interface TaskTemplateSet {
  id: string;
  name: string;
  description: string | null;
  createdByUserId: string | null;
  createdBy?: {
    id: string;
    firstName: string | null;
    lastName: string | null;
    email: string;
  } | null;
  version: number;
  createdAt: string;
  updatedAt: string;
  isEditable: boolean;
  linkedProjects: { id: string; name: string; status: string }[];
}

interface TaskTemplateSetsQueryResult {
  taskTemplateSets: {
    items: TaskTemplateSet[];
    totalCount: number;
    hasNextPage: boolean;
  };
}

interface TaskTemplateSetCreateInput {
  name: string;
  description?: string | null;
}

interface TaskTemplateSetUpdateInput {
  id: string;
  version: number;
  name?: string;
  description?: string | null;
}

interface TemplateTaskCreateInput {
  name: string;
  description: string | null;
  taskTemplateSetId: string | null;
  templateWorkSectionId: string | null;
  templateProcurementPackageId: string;
  area: TaskAreaType;
  parentTemplateTaskId: string | null;
  congridInspectionTemplateId: string | null;
  startTimingOffsetDays: number | null;
  endTimingOffsetDays: number | null;
}

export function useTaskTemplates() {
  const { data, loading, error, refetch } = useQuery<TaskTemplateSetsQueryResult>(
    GET_TASK_TEMPLATE_SETS,
    {
      fetchPolicy: "cache-and-network",
    }
  );

  const [createTemplateSetMutation, { loading: creating }] = useMutation(
    CREATE_TASK_TEMPLATE_SET,
    {
      refetchQueries: [{ query: GET_TASK_TEMPLATE_SETS }],
    }
  );

  const [updateTemplateSetMutation] = useMutation(UPDATE_TASK_TEMPLATE_SET, {
    refetchQueries: [{ query: GET_TASK_TEMPLATE_SETS }],
  });

  const [deleteTemplateSetMutation] = useMutation(DELETE_TASK_TEMPLATE_SET, {
    refetchQueries: [{ query: GET_TASK_TEMPLATE_SETS }],
  });

  const [duplicateTemplateSetMutation, { loading: duplicating }] = useMutation(
    DUPLICATE_TASK_TEMPLATE_SET,
    {
      refetchQueries: [{ query: GET_TASK_TEMPLATE_SETS }],
    }
  );

  const [saveTemplateSetTasksMutation, { loading: savingTasks }] = useMutation(
    SAVE_TEMPLATE_SET_TASKS
  );

  const [bulkCreateTasksMutation] = useMutation(BULK_CREATE_TEMPLATE_TASKS);
  const [bulkDeleteTasksMutation] = useMutation(BULK_DELETE_TEMPLATE_TASKS_BY_SET);
  const [bulkDeleteProcurementPackagesMutation] = useMutation(BULK_DELETE_TEMPLATE_PROCUREMENT_PACKAGES_BY_SET);
  const [bulkDeleteWorkSectionsMutation] = useMutation(BULK_DELETE_TEMPLATE_WORK_SECTIONS_BY_SET);
  const [bulkCreateProcurementPackagesMutation] = useMutation(BULK_CREATE_TEMPLATE_PROCUREMENT_PACKAGES);
  const [bulkCreateWorkSectionsMutation] = useMutation(BULK_CREATE_TEMPLATE_WORK_SECTIONS);

  const [createProcurementPackageMutation] = useMutation(CREATE_PROCUREMENT_PACKAGE);
  const [createWorkSectionMutation] = useMutation(CREATE_WORK_SECTION);
  const [updateWorkSectionMutation] = useMutation(UPDATE_WORK_SECTION);

  const createTemplateSet = async (input: TaskTemplateSetCreateInput) => {
    const result = await createTemplateSetMutation({ variables: { input } });
    return result.data?.createTaskTemplateSet;
  };

  const updateTemplateSet = async (input: TaskTemplateSetUpdateInput) => {
    const result = await updateTemplateSetMutation({ variables: { input } });
    return result.data?.updateTaskTemplateSet;
  };

  const duplicateTemplateSet = async (input: {
    id: string;
    name: string;
    description?: string | null;
  }) => {
    const result = await duplicateTemplateSetMutation({ variables: { input } });
    return result.data?.duplicateTaskTemplateSet;
  };

  const saveTemplateSetTasks = async (input: {
    taskTemplateSetId: string;
    creates: unknown[];
    updates: unknown[];
    deleteIds: string[];
  }) => {
    const result = await saveTemplateSetTasksMutation({ variables: input });
    return result.data?.saveTemplateSetTasks as
      | {
          success: boolean;
          message: string;
          errors: string[];
          createdCount: number;
          updatedCount: number;
          deletedCount: number;
        }
      | undefined;
  };

  const deleteTemplateSet = async (id: string) => {
    const result = await deleteTemplateSetMutation({ variables: { id } });
    return result.data?.deleteTaskTemplateSet;
  };

  const bulkCreateTasks = async (
    taskTemplateSetId: string,
    tasks: TemplateTaskCreateInput[]
  ) => {
    const result = await bulkCreateTasksMutation({
      variables: { taskTemplateSetId, tasks },
    });
    return result.data?.bulkCreateTemplateTasks;
  };

  const bulkDeleteTasksBySet = async (taskTemplateSetId: string) => {
    const result = await bulkDeleteTasksMutation({
      variables: { taskTemplateSetId },
    });
    return result.data?.bulkDeleteTemplateTasksBySet;
  };

  const bulkDeleteProcurementPackagesBySet = async (taskTemplateSetId: string) => {
    const result = await bulkDeleteProcurementPackagesMutation({
      variables: { taskTemplateSetId },
    });
    return result.data?.bulkDeleteTemplateProcurementPackagesBySet;
  };

  const bulkDeleteWorkSectionsBySet = async (taskTemplateSetId: string) => {
    const result = await bulkDeleteWorkSectionsMutation({
      variables: { taskTemplateSetId },
    });
    return result.data?.bulkDeleteTemplateWorkSectionsBySet;
  };

  const bulkCreateProcurementPackages = async (
    taskTemplateSetId: string,
    packages: Array<{ code: string; name: string }>
  ) => {
    const result = await bulkCreateProcurementPackagesMutation({
      variables: { taskTemplateSetId, packages },
    });
    return result.data?.bulkCreateTemplateProcurementPackages;
  };

  const bulkCreateWorkSections = async (
    taskTemplateSetId: string,
    workSections: Array<{ code: string; name: string; templateProcurementPackageId?: string }>
  ) => {
    const result = await bulkCreateWorkSectionsMutation({
      variables: { taskTemplateSetId, workSections },
    });
    return result.data?.bulkCreateTemplateWorkSections;
  };

  const createProcurementPackage = async (input: {
    code: string;
    name: string;
    taskTemplateSetId: string;
  }) => {
    const result = await createProcurementPackageMutation({ variables: { input } });
    return result.data?.createTemplateProcurementPackage;
  };

  const createWorkSection = async (input: {
    code: string;
    name: string;
    taskTemplateSetId: string;
  }) => {
    const result = await createWorkSectionMutation({ variables: { input } });
    return result.data?.createTemplateWorkSection;
  };

  const updateWorkSection = async (input: {
    id: string;
    version: number;
    templateProcurementPackageId: string;
  }) => {
    const result = await updateWorkSectionMutation({ variables: { input } });
    return result.data?.updateTemplateWorkSection;
  };

  // State for creation process
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  const createTemplateSetWithHierarchy = async (
    name: string,
    description: string | null,
    excelData: ExcelData | null
  ) => {
    if (isSubmitting) return { success: false, error: "Already submitting" };

    setIsSubmitting(true);
    setCreateError(null);

    let templateSetId: string | null = null;
    let createdPackageIds: string[] = [];
    let createdWorkSectionIds: string[] = [];

    try {
      // Step 1: Create the template set container
      const setResult = await createTemplateSetMutation({
        variables: {
          input: {
            name,
            description,
          },
        },
      });

      if (!setResult.data?.createTaskTemplateSet?.success) {
        throw new Error(
          setResult.data?.createTaskTemplateSet?.message || "Failed to create template set"
        );
      }

      templateSetId = setResult.data.createTaskTemplateSet.taskTemplateSet.id;

      // Step 2: If we have Excel data, create the hierarchy using bulk mutations
      if ((excelData?.procurementPackages?.length ?? 0) > 0) {
        // Collect all unique procurement packages
        const allPackages = excelData?.procurementPackages.map((pp) => ({
          code: pp.procurementPackageCode,
          name: pp.procurementPackageName,
        }));

        // Step 2a: Bulk create all procurement packages
        const ppResult = await bulkCreateProcurementPackagesMutation({
          variables: {
            taskTemplateSetId: templateSetId,
            packages: allPackages,
          },
        });

        if (!ppResult.data?.bulkCreateTemplateProcurementPackages?.success) {
          throw new Error(
            ppResult.data?.bulkCreateTemplateProcurementPackages?.message ||
              "Failed to create procurement packages"
          );
        }

        // Build mapping from package code to ID
        const packageIdMap = new Map<string, string>();
        const createdPackages = ppResult.data.bulkCreateTemplateProcurementPackages.templateProcurementPackages;
        for (const pkg of createdPackages) {
          packageIdMap.set(pkg.code, pkg.id);
          createdPackageIds.push(pkg.id);
        }

        // Collect all unique work sections with their package references
        const allWorkSections: Array<{
          code: string;
          name: string;
          templateProcurementPackageId: string;
        }> = [];
        const seenWorkSectionCodes = new Set<string>();

        for (const procurementPackage of excelData?.procurementPackages || []) {
          const packageId = packageIdMap.get(procurementPackage.procurementPackageCode);
          if (!packageId) {
            console.warn(
              `Package ID not found for code: ${procurementPackage.procurementPackageCode}`
            );
            continue;
          }

          for (const workSection of procurementPackage.workSections) {
            if (
              workSection.workSectionCode &&
              !seenWorkSectionCodes.has(workSection.workSectionCode)
            ) {
              allWorkSections.push({
                code: workSection.workSectionCode,
                name: workSection.workSectionName || workSection.workSectionCode,
                templateProcurementPackageId: packageId,
              });
              seenWorkSectionCodes.add(workSection.workSectionCode);
            }
          }
        }

        // Step 2b: Bulk create all work sections
        if (allWorkSections.length > 0) {
          const wsResult = await bulkCreateWorkSectionsMutation({
            variables: {
              taskTemplateSetId: templateSetId,
              workSections: allWorkSections,
            },
          });

          if (!wsResult.data?.bulkCreateTemplateWorkSections?.success) {
            // Automatic cleanup: delete created packages
            console.warn("Work section creation failed, cleaning up created packages...");
            await cleanupCreatedPackages(createdPackageIds, templateSetId);
            throw new Error(
              wsResult.data?.bulkCreateTemplateWorkSections?.message ||
                "Failed to create work sections"
            );
          }

          // Build mapping from work section code to ID
          var workSectionIdMap = new Map<string, string>();
          const createdWorkSections = wsResult.data.bulkCreateTemplateWorkSections.templateWorkSections;
          for (const ws of createdWorkSections) {
            workSectionIdMap.set(ws.code, ws.id);
            createdWorkSectionIds.push(ws.id);
          }
        } else {
          var workSectionIdMap = new Map<string, string>();
        }

        // Step 2c: Build parent tasks and subtask queue.
        // Subtasks reference their parent by index into parentTasksInput; after
        // the bulk create, createdParentTasks[parentIndex].id is the parent UUID.
        const parentTasksInput: TemplateTaskCreateInput[] = [];
        const subtaskQueue: Array<{
          subtask: Subtask;
          parentIndex: number;
          procurementPackageId: string;
          workSectionId: string | null;
        }> = [];

        for (const procurementPackage of excelData?.procurementPackages || []) {
          const packageId = packageIdMap.get(procurementPackage.procurementPackageCode);
          if (!packageId) continue;

          // Create work section tasks
          for (const workSection of procurementPackage.workSections) {
            const workSectionId = workSectionIdMap.get(workSection.workSectionCode);

            for (const wsTask of workSection.inspectionTasks) {
              parentTasksInput.push({
                name: wsTask.taskTitle,
                description: null,
                taskTemplateSetId: templateSetId,
                templateWorkSectionId: workSectionId || null,
                templateProcurementPackageId: packageId,
                area: TaskAreaType.CONSTRUCTION,
                parentTemplateTaskId: null,
                congridInspectionTemplateId: wsTask.congridId,
                startTimingOffsetDays: wsTask.deadlineAtDaysBeforeStart ?? null,
                endTimingOffsetDays: wsTask.deadlineAtDaysBeforeEnd ?? null,
              });
            }

            for (const dtTask of workSection.documentTasks) {
              parentTasksInput.push({
                name: dtTask.taskTitle,
                description: null,
                taskTemplateSetId: templateSetId,
                templateWorkSectionId: workSectionId || null,
                templateProcurementPackageId: packageId,
                area: TaskAreaType.CONSTRUCTION,
                parentTemplateTaskId: null,
                congridInspectionTemplateId: null,
                startTimingOffsetDays: dtTask.deadlineAtDaysBeforeStart ?? null,
                endTimingOffsetDays: dtTask.deadlineAtDaysBeforeEnd ?? null,
              });
              const parentIndex = parentTasksInput.length - 1;

              for (const subtask of dtTask.subtasks) {
                subtaskQueue.push({
                  subtask,
                  parentIndex,
                  procurementPackageId: packageId,
                  workSectionId: workSectionId || null,
                });
              }
            }
          }

          // Create normal tasks with subtasks
          for (const task of procurementPackage.tasks) {
            parentTasksInput.push({
              name: task.taskTitle,
              description: null,
              taskTemplateSetId: templateSetId,
              templateWorkSectionId: null,
              area: TaskAreaType.CONSTRUCTION,
              templateProcurementPackageId: packageId,
              parentTemplateTaskId: null,
              congridInspectionTemplateId: null,
              startTimingOffsetDays: task.deadlineAtDaysBeforeStart ?? null,
              endTimingOffsetDays: task.deadlineAtDaysBeforeEnd ?? null,
            });
            const parentIndex = parentTasksInput.length - 1;

            for (const subtask of task.subtasks) {
              subtaskQueue.push({
                subtask,
                parentIndex,
                procurementPackageId: packageId,
                workSectionId: null,
              });
            }
          }
        }

        // Step 2d: Bulk create all parent tasks.
        // Chunked to stay under backend's max_batch_size (1000 per call in
        // TemplateTaskService.bulk_create). Order is preserved across chunks
        // so createdParentTasks[i] still maps to parentTasksInput[i].
        const TASK_CHUNK_SIZE = 1000;
        const createdParentTasks: Array<{ id: string }> = [];

        for (let i = 0; i < parentTasksInput.length; i += TASK_CHUNK_SIZE) {
          const chunk = parentTasksInput.slice(i, i + TASK_CHUNK_SIZE);
          const bulkResult = await bulkCreateTasksMutation({
            variables: {
              taskTemplateSetId: templateSetId,
              tasks: chunk,
            },
          });

          if (!bulkResult.data?.bulkCreateTemplateTasks?.success) {
            await cleanupCreatedWorkSections(createdWorkSectionIds, templateSetId);
            await cleanupCreatedPackages(createdPackageIds, templateSetId);
            throw new Error(
              bulkResult.data?.bulkCreateTemplateTasks?.message || "Failed to create tasks"
            );
          }

          createdParentTasks.push(...bulkResult.data.bulkCreateTemplateTasks.templateTasks);
        }

        // Step 2e: Bulk create all subtasks, resolving parents by input index.
        // bulk_create on the backend preserves input order, so
        // createdParentTasks[i] corresponds to parentTasksInput[i].
        const subtasksInput: TemplateTaskCreateInput[] = [];
        const skippedSubtasks: string[] = [];

        for (const { subtask, parentIndex, procurementPackageId, workSectionId } of subtaskQueue) {
          const parent = createdParentTasks[parentIndex];
          if (!parent) {
            skippedSubtasks.push(subtask.subtaskTitle);
            continue;
          }

          subtasksInput.push({
            name: subtask.subtaskTitle,
            description: subtask.description || null,
            taskTemplateSetId: templateSetId,
            templateWorkSectionId: workSectionId,
            templateProcurementPackageId: procurementPackageId,
            area: TaskAreaType.CONSTRUCTION,
            parentTemplateTaskId: parent.id,
            congridInspectionTemplateId: null,
            startTimingOffsetDays: null,
            endTimingOffsetDays: null,
          });
        }

        if (skippedSubtasks.length > 0) {
          throw new Error(
            `Alitehtävien päätehtävää ei löytynyt (${skippedSubtasks.length} kpl): ${skippedSubtasks.slice(0, 5).join(", ")}${skippedSubtasks.length > 5 ? "…" : ""}`
          );
        }

        for (let i = 0; i < subtasksInput.length; i += TASK_CHUNK_SIZE) {
          const chunk = subtasksInput.slice(i, i + TASK_CHUNK_SIZE);
          const subtaskResult = await bulkCreateTasksMutation({
            variables: {
              taskTemplateSetId: templateSetId,
              tasks: chunk,
            },
          });

          if (!subtaskResult.data?.bulkCreateTemplateTasks?.success) {
            throw new Error(
              subtaskResult.data?.bulkCreateTemplateTasks?.message ||
                "Alitehtävien luonti epäonnistui"
            );
          }
        }
      }

      return { success: true, templateSetId };
    } catch (error: any) {
      console.error("Failed to create template set with hierarchy:", error);

      // Automatic cleanup on any error
      if (createdWorkSectionIds.length > 0) {
        await cleanupCreatedWorkSections(createdWorkSectionIds, templateSetId);
      }
      if (createdPackageIds.length > 0) {
        await cleanupCreatedPackages(createdPackageIds, templateSetId);
      }

      const errorMsg =
        error.graphQLErrors?.[0]?.message || error.message || "Tehtäväpohjan luonti epäonnistui";
      setCreateError(errorMsg);
      return { success: false, error: errorMsg };
    } finally {
      setIsSubmitting(false);
    }
  };

  // Cleanup helper functions for automatic rollback
  const cleanupCreatedPackages = async (packageIds: string[], templateSetId: string | null) => {
    if (packageIds.length === 0 || !templateSetId) return;
    try {
      console.log(`Cleaning up ${packageIds.length} created packages...`);
      await bulkDeleteProcurementPackagesMutation({
        variables: { taskTemplateSetId: templateSetId },
      });
    } catch (e) {
      console.error("Failed to cleanup packages:", e);
    }
  };

  const cleanupCreatedWorkSections = async (workSectionIds: string[], templateSetId: string | null) => {
    if (workSectionIds.length === 0 || !templateSetId) return;
    try {
      console.log(`Cleaning up ${workSectionIds.length} created work sections...`);
      await bulkDeleteWorkSectionsMutation({
        variables: { taskTemplateSetId: templateSetId },
      });
    } catch (e) {
      console.error("Failed to cleanup work sections:", e);
    }
  };

  const resetCreateError = () => setCreateError(null);

  return {
    taskTemplateSets: (data?.taskTemplateSets?.items || []) as TaskTemplateSet[],
    totalCount: data?.taskTemplateSets?.totalCount || 0,
    hasNextPage: data?.taskTemplateSets?.hasNextPage || false,
    loading,
    error,
    refetch,
    creating,
    isSubmitting,
    createError,
    resetCreateError,
    // CRUD operations
    createTemplateSet,
    updateTemplateSet,
    deleteTemplateSet,
    duplicateTemplateSet,
    duplicating,
    saveTemplateSetTasks,
    savingTasks,
    // Bulk operations
    bulkCreateTasks,
    bulkDeleteTasksBySet,
    bulkDeleteProcurementPackagesBySet,
    bulkDeleteWorkSectionsBySet,
    bulkCreateProcurementPackages,
    bulkCreateWorkSections,
    // Procurement packages and work sections
    createProcurementPackage,
    createWorkSection,
    updateWorkSection,
    // Complex creation with hierarchy
    createTemplateSetWithHierarchy,
    // Query ref for external use
    fetchTaskTemplateSetsQuery: GET_TASK_TEMPLATE_SETS,
  };
}
